'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { formatDuration } from '@/lib/workTime';
import { timerApi, type ActiveTimer } from '@/lib/api/workServices';

export type TimerStartPayload = { projectId: string; taskId?: string; taskTitle?: string };
export type TimerStopPayload = string | { description?: string; isBillable?: boolean };

type TimeTrackerContextValue = {
  timer: ActiveTimer | null;
  displaySeconds: number;
  displayLabel: string;
  loading: boolean;
  conflict: ActiveTimer | null;
  refresh: () => Promise<void>;
  start: (payload: TimerStartPayload) => Promise<boolean>;
  pause: () => Promise<void>;
  resume: () => Promise<void>;
  stop: (payload?: TimerStopPayload) => Promise<void>;
  stopAndSwitch: (payload?: TimerStopPayload) => Promise<boolean>;
  clearConflict: () => void;
};

const TimeTrackerContext = createContext<TimeTrackerContextValue | null>(null);

function liveSeconds(timer: ActiveTimer | null): number {
  if (!timer) return 0;
  if (timer.status !== 'running') return timer.durationSeconds;
  const last = new Date(timer.resumedAt || timer.startedAt).getTime();
  const extra = Math.max(0, Math.floor((Date.now() - last) / 1000));
  return timer.accumulatedSeconds + extra;
}

function isConflict(
  result: ActiveTimer | { conflict: true; activeTimer: ActiveTimer }
): result is { conflict: true; activeTimer: ActiveTimer } {
  return Boolean(result && 'conflict' in result && result.conflict);
}

export function TimeTrackerProvider({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const [timer, setTimer] = useState<ActiveTimer | null>(null);
  const [loading, setLoading] = useState(false);
  const [conflict, setConflict] = useState<ActiveTimer | null>(null);
  const [tick, setTick] = useState(0);
  const queuedStart = useRef<TimerStartPayload | null>(null);
  const timerRef = useRef<ActiveTimer | null>(null);
  timerRef.current = timer;

  const refresh = useCallback(async () => {
    if (!user) {
      setTimer(null);
      return;
    }
    try {
      const active = await timerApi.getActive();
      setTimer(active);
    } catch {
      setTimer(null);
    }
  }, [user]);

  useEffect(() => {
    if (authLoading) return;
    refresh();
  }, [authLoading, refresh]);

  useEffect(() => {
    if (!timer || timer.status !== 'running') return;
    const id = window.setInterval(() => setTick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, [timer]);

  const displaySeconds = useMemo(() => liveSeconds(timer), [timer, tick]);

  const start = useCallback(async (payload: TimerStartPayload) => {
    const current = timerRef.current;
    if (current && payload.taskId && current.taskId === payload.taskId) {
      if (current.status === 'running') {
        toast.message('Already tracking this task');
        return true;
      }
    }
    setLoading(true);
    try {
      const result = await timerApi.start(payload);
      if (isConflict(result)) {
        queuedStart.current = payload;
        setConflict(result.activeTimer);
        return false;
      }
      queuedStart.current = null;
      setTimer(result);
      toast.success(
        current && current.taskId === payload.taskId
          ? current.status === 'paused'
            ? 'Timer resumed'
            : 'Timer restarted'
          : 'Timer started'
      );
      return true;
    } catch (error: any) {
      toast.error(error.message || 'Could not start timer');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const pause = useCallback(async () => {
    setLoading(true);
    try {
      const next = await timerApi.pause();
      setTimer(next);
    } catch (error: any) {
      toast.error(error.message || 'Could not pause');
    } finally {
      setLoading(false);
    }
  }, []);

  const resume = useCallback(async () => {
    setLoading(true);
    try {
      const next = await timerApi.resume();
      setTimer(next);
    } catch (error: any) {
      toast.error(error.message || 'Could not resume');
    } finally {
      setLoading(false);
    }
  }, []);

  const stop = useCallback(async (payload?: TimerStopPayload) => {
    setLoading(true);
    try {
      const result = await timerApi.stop(payload);
      setTimer(null);
      setConflict(null);
      toast.success(
        result.continued
          ? `Continued this task · ${result.durationLabel} added`
          : `Time saved ${result.durationLabel}`
      );
    } catch (error: any) {
      toast.error(error.message || 'Could not stop timer');
    } finally {
      setLoading(false);
    }
  }, []);

  const stopAndSwitch = useCallback(async (payload?: TimerStopPayload) => {
    const queued = queuedStart.current;
    setLoading(true);
    try {
      const result = await timerApi.stop(payload);
      setTimer(null);
      setConflict(null);
      toast.success(
        result.continued
          ? `Continued this task · ${result.durationLabel} added`
          : `Time saved ${result.durationLabel}`
      );
      if (!queued) return false;
      queuedStart.current = null;
      const next = await timerApi.start(queued);
      if (isConflict(next)) {
        queuedStart.current = queued;
        setConflict(next.activeTimer);
        return false;
      }
      setTimer(next);
      toast.success('Timer started');
      return true;
    } catch (error: any) {
      toast.error(error.message || 'Could not switch timer');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const value = useMemo<TimeTrackerContextValue>(
    () => ({
      timer,
      displaySeconds,
      displayLabel: formatDuration(displaySeconds),
      loading,
      conflict,
      refresh,
      start,
      pause,
      resume,
      stop,
      stopAndSwitch,
      clearConflict: () => {
        queuedStart.current = null;
        setConflict(null);
      },
    }),
    [timer, displaySeconds, loading, conflict, refresh, start, pause, resume, stop, stopAndSwitch]
  );

  return <TimeTrackerContext.Provider value={value}>{children}</TimeTrackerContext.Provider>;
}

export function useTimeTracker() {
  const ctx = useContext(TimeTrackerContext);
  if (!ctx) throw new Error('useTimeTracker must be used within TimeTrackerProvider');
  return ctx;
}
